import React, {
  ChangeEventHandler,
  FocusEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Col, Form, Input, theme } from 'antd';
import { getIn, useFormikContext } from 'formik';
import { UndoOutlined } from '@ant-design/icons';
import useStyles from './styles';

interface Props {
  label: string;
  fieldName: string;
  type?: string;
  min?: number;
  max?: number;
  onBlur?: (value: string) => void;
  prefix?: string;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  xs?: number | undefined;
  sm?: number | undefined;
  md?: number | undefined;
  lg?: number | undefined;
  xl?: number | undefined;
  xxl?: number | undefined;
  showReset?: boolean;
}

const { TextArea } = Input;

const TextAreaField = ({
  label,
  fieldName,
  onBlur,
  prefix,
  disabled = false,
  placeholder,
  maxLength,
  xs,
  sm,
  md,
  lg,
  xl,
  xxl,
  showReset = true,
}: Props) => {
  const { values, initialValues, errors, setFieldValue, setFieldTouched } =
    useFormikContext<any>();
  const { token } = theme.useToken();
  const classes = useStyles({ theme: token });
  const value =
    getIn(values, fieldName) instanceof Object ? '' : getIn(values, fieldName);
  const error =
    getIn(errors, fieldName) instanceof Object ? '' : getIn(errors, fieldName);
  const initialValue =
    getIn(initialValues, fieldName) instanceof Object
      ? ''
      : getIn(initialValues, fieldName);

  const hasChanged = String(value ?? '') !== String(initialValue ?? '');

  const [input, setInput] = useState(value);

  useEffect(() => {
    setInput(value);
  }, [value]);

  const handleBlur: FocusEventHandler<HTMLTextAreaElement> = useCallback(
    (e) => {
      if (onBlur) {
        onBlur(e.target.value);
      }
      setFieldTouched(fieldName, true);
      setFieldValue(fieldName, e.target.value);
    },
    [fieldName, onBlur, setFieldTouched, setFieldValue],
  );

  const handleRestart = useCallback(() => {
    setFieldValue(fieldName, initialValue);
    setFieldTouched(fieldName, false);
  }, [fieldName, initialValue, setFieldValue, setFieldTouched]);

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = useCallback(
    (e): void => {
      setInput(e.target.value);
    },
    [setInput],
  );

  const getValidateStatus = () => {
    let validateStatus:
      | ''
      | 'error'
      | 'warning'
      | 'success'
      | 'validating'
      | undefined;
    if (hasChanged && showReset) {
      validateStatus = 'warning';
    }
    if (error) {
      validateStatus = 'error';
    }
    return validateStatus;
  };

  const validateStatus = getValidateStatus();

  return (
    <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
      <div style={{ textAlign: 'left' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          <span>{label}</span>
          {hasChanged && showReset && (
            <div>
              <UndoOutlined onClick={handleRestart} />
            </div>
          )}
        </div>
        <div className={classes.topSelectEffect}>
          <Form.Item
            validateStatus={validateStatus}
            help={error}
            style={{ margin: 0 }}
          >
            <TextArea
              name={fieldName}
              prefix={prefix}
              onChange={handleChange}
              value={input}
              onBlur={handleBlur}
              style={{
                width: '100%',
                marginTop: '6px',
              }}
              placeholder={placeholder || label}
              disabled={disabled}
              showCount
              maxLength={maxLength}
            />
          </Form.Item>
        </div>
      </div>
    </Col>
  );
};

export default TextAreaField;
